import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

export function Tabs(
  props: ComponentProps<typeof TabsPrimitive.Root>,
): React.ReactNode {
  return <TabsPrimitive.Root data-slot="tabs" {...props} />;
}

/**
 * The tab row. Height is a **minimum**, not a fixed value: a caller with more triggers than fit adds
 * `flex-wrap`, and a fixed height would lay the second row out while the box stayed 40px — so the
 * wrapped triggers render outside their container and are covered by the content below. That is how
 * the developer page's `Instructions` and `Project Memory` tabs disappeared under ~660px of width.
 * `min-h-10` renders identically wherever the row fits, and only grows where it was already broken.
 */
export function TabsList({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>): React.ReactNode {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'inline-flex min-h-10 items-center justify-start gap-1 rounded-lg bg-muted/50 p-1 text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

export function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>): React.ReactNode {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-elev-1',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>): React.ReactNode {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        'mt-3 outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    />
  );
}
